import { Alert, Box, Skeleton, Stack } from "@mui/material";
import { useSupabaseLoader } from "../../../../../../server/supabase/hooks";
import { useGetPollsWithOptions } from "../../../../../poll/hooks/use-poll-with-options";
import { useLexicalContext } from "../../../lexical-context";
import { ChatPollComponent } from "./chat-poll-component";
import { CustomPollComponent } from "./custom-poll-component";
import { NonCustomPollComponent } from "./non-custom-poll-component";
import { PollWrapper } from "./poll-wrapper";

import type { UUID } from "../../../../../../utils/uuid";
import type { PollWithOptions } from "../../../../../poll/server/supabase-poll-service";
import type { PollSchema } from "../../../plugins/poll-plugin/types";
import type { ElementFormatType, NodeKey } from "lexical";
import type { FC } from "react";

export const PollComponent: FC<{
  nodeKey: NodeKey;
  pollId: UUID;
  answer_type: PollSchema["answer_type"];
  refresh_id: UUID;
  format?: ElementFormatType;
}> = ({ nodeKey, pollId, answer_type, refresh_id, format }) => {
  const { format: channelType } = useLexicalContext();

  const getPolls = useGetPollsWithOptions();

  const {
    data: polls,
    loading,
    error,
  } = useSupabaseLoader(async () => await getPolls([pollId]), [pollId, refresh_id]);

  if (loading) return <Skeleton variant="rectangular" width="100%" height={100} />;

  const poll = polls?.[pollId];
  if (error || !poll)
    return (
      <Alert severity="error" variant="outlined" sx={{ width: "100%" }}>
        Unable to load poll
      </Alert>
    );

  return (
    <PollWrapper nodeKey={nodeKey} format={format}>
      <Stack direction="column" spacing={1}>
        <p>
          <strong className="editor-text-bold">{poll.poll.question}</strong>
        </p>

        {channelType === "email" && <EmailPollComponent poll={poll} answer_type={answer_type} />}

        {channelType !== "email" && <ChatPollComponent nodeKey={nodeKey} poll={poll} />}

        <Box>
          {poll.poll.helper_text && <p style={{ fontSize: "0.8em" }}>{poll.poll.helper_text}</p>}
        </Box>
      </Stack>
    </PollWrapper>
  );
};

const EmailPollComponent: FC<{ poll: PollWithOptions; answer_type: PollSchema["answer_type"] }> = ({
  poll,
  answer_type,
}) => {
  if (answer_type.type === "custom") {
    return <CustomPollComponent poll={poll} answer_type={answer_type} />;
  }

  return <NonCustomPollComponent poll={poll} />;
};
